import request from "../axios";
import {
  buildFeedbackPayload,
  buildHistoryQuery,
  buildQualityQuery,
  FeedbackDimension,
  FeedbackErrorType,
  HistoryDays,
  QualityFeedbackPayload,
  QualityQuery,
  QualityQueue,
  StandardIssueType,
} from "../utils/mobileQuality";

export interface MobileQualityDashboard {
  date: string;
  capturedCount: number;
  evaluatedCount: number;
  qualifiedCount: number;
  unqualifiedCount: number;
  captureIssueCount: number;
  pendingReviewCount: number;
  trialCount?: number;
  qualifiedRate: number;
}

export interface MobileQualityRecord {
  id: string;
  capturedAt: string;
  storeId: string;
  storeName?: string;
  dishId?: string;
  dishName?: string;
  captureImageUrl?: string;
  qualityResult?: "QUALIFIED" | "UNQUALIFIED" | "AI_REVIEW_REQUIRED";
  dishMatchResult?: "SAME" | "DIFFERENT" | "UNCERTAIN";
  qualityDimensions?: Record<string, string>;
  failedDimensions?: string[];
  captureStatus: "VALID" | "INVALID_CAPTURE";
  aiReason?: string;
  temperatureCelsius?: number;
  aiJudgeFeedback?: 0 | 1 | null;
  aiJudgeFeedbackNote?: string;
  feedbackErrorType?: FeedbackErrorType | "UNSPECIFIED";
  correctedDishId?: string;
  correctedDishName?: string;
  dishMissing?: boolean;
  expectedQualityResult?: "QUALIFIED" | "UNQUALIFIED" | "AI_REVIEW_REQUIRED";
  feedbackDisputedDimensions?: FeedbackDimension[];
  feedbackActualProblemDimensions?: FeedbackDimension[];
  feedbackStandardIssueTypes?: StandardIssueType[];
  aiJudgeFeedbackAt?: string;
  standardImageUrl?: string;
  trialRuleStatus?: "UNCONFIGURED" | "DRAFT" | "TRIAL" | "PUBLISHED";
  trialResult?: TrialQualityResult | null;
}

export interface TrialQualityItem {
  id: string;
  name: string;
  type: "REQUIRED" | "OPTIONAL" | "REMINDER";
  maxScore: number;
  score: number | null;
  deduction?: number | null;
  direction?: "EXCESS" | "INSUFFICIENT" | "DARK" | "LIGHT" | "MISSING" | "NONE" | "OTHER" | "UNKNOWN";
  state: "PASS" | "MINOR" | "MAJOR" | "ABSENT" | "OCCLUDED" | "UNKNOWN";
  reason?: string;
}

export interface TrialQualityResult {
  runId: string;
  trialLabel: "试运行" | "正式评分";
  ruleRevision: number;
  outcome: "PASS" | "FAIL" | "REVIEW" | "PENDING" | "ERROR";
  totalScore: number | null;
  passAt: number;
  failBelow: number;
  reason?: string;
  items: TrialQualityItem[];
  feedbackAgreed?: 0 | 1 | null;
  feedbackNote?: string;
  trialStandardImageUrl?: string | null;
  rule?: { config?: { checks?: Array<{
    id: string; standard?: string; allowed?: string;
    minorDescription?: string; majorDescription?: string; unknownDescription?: string;
  }> } };
  reviewRequest?: { id: string; reason_code: string; note?: string; created_at?: string } | null;
  humanReviews?: Array<{
    id: string; revision: number; decision: "RELEASE" | "ADJUST" | "RETAKE";
    reason: string; reviewer_name: string; correctedItems?: Array<{ id: string; state: string }>;
    created_at?: string;
  }>;
  retakes?: Array<{ new_record_id: string; reason: string; linked_at?: string }>;
}

export interface MobileQualityHistory {
  days: HistoryDays;
  startDate: string;
  endDate: string;
  capturedCount: number;
  evaluatedCount: number;
  qualifiedCount: number;
  unqualifiedCount: number;
  captureIssueCount: number;
  pendingReviewCount: number;
  qualifiedRate: number;
  previousEvaluatedCount: number;
  previousQualifiedRate: number;
  qualifiedRateChange: number;
  items: MobileQualityDashboard[];
}

export interface PageResult<T> {
  records: T[];
  total: number;
  current: number;
  size: number;
  pages: number;
}

export interface FeedbackDishOption {
  id: string;
  name: string;
}

export async function getQualityDashboard(params: {
  date: string;
  storeId?: string | number;
  dishId?: string | number;
}): Promise<MobileQualityDashboard> {
  const { data } = await request.get("/mobile/quality/dashboard", params);
  return data;
}

export async function getQualityHistory(params: {
  days: HistoryDays;
  storeId?: string | number;
  dishId?: string | number;
}): Promise<MobileQualityHistory> {
  const { data } = await request.get("/mobile/quality/history/days", buildHistoryQuery(params));
  return data;
}

export async function getQualityRecords(query: QualityQuery): Promise<PageResult<MobileQualityRecord>> {
  const { data } = await request.get("/mobile/quality/records", buildQualityQuery(query));
  return data;
}

export async function getQualityRecord(recordId: string): Promise<MobileQualityRecord> {
  const { data } = await request.get(`/mobile/quality/records/${recordId}`);
  return data;
}

export async function getFeedbackDishOptions(): Promise<FeedbackDishOption[]> {
  const { data } = await request.get("/dish/list/all", { name: null });
  const values = data?.dishList || data?.records || (Array.isArray(data) ? data : []);
  return values
    .map((item: any) => ({ id: String(item.id), name: item.name || item.dishName || "" }))
    .filter((item: FeedbackDishOption) => item.id && item.name);
}

export async function submitQualityFeedback(recordId: string, payload: QualityFeedbackPayload) {
  const { data } = await request.post(
    `/mobile/quality/records/${recordId}/feedback`,
    buildFeedbackPayload(payload)
  );
  return data as MobileQualityRecord;
}

export async function submitTrialQualityFeedback(recordId: string, agreed: boolean, note: string) {
  const { data } = await request.post(`/mobile/quality/records/${recordId}/trial-feedback`, { agreed, note });
  return data as MobileQualityRecord;
}

export type TrialReviewReason = "LIGHTING" | "IMAGE_QUALITY" | "STANDARD_MISMATCH" | "SCORING" | "OTHER";

export async function requestTrialQualityReview(recordId: string, reasonCode: TrialReviewReason,
                                                note: string, idempotencyKey: string) {
  const { data } = await request.post(`/mobile/quality/records/${recordId}/review-requests`, {
    reasonCode, note, idempotencyKey,
  });
  return data as MobileQualityRecord;
}

export type { QualityQueue };
